import { LoaderPreview } from "./LoaderPreview";
import { StartOverlayVariantB } from "./StartOverlayVariantB";
import { readVariant } from "../lib/variant";
import type { LoadFlags } from "../types";

export function StartOverlay(props: {
  previewUrl: string;
  visible: boolean;
  ready: boolean;
  sceneFallback: boolean;
  canConfigure: boolean;
  loadFlags: LoadFlags;
  error?: string;
  canInstall: boolean;
  isIOS: boolean;
  onInstall: () => void;
  onAlign: () => void;
  onConfig: () => void;
  onRetryScene: () => void;
  onPlay: () => void;
  onWatchRoute: () => void;
}) {
  // A/B toggle: `?variant=b` renders the alternate centered hero.
  if (readVariant() === "b") return <StartOverlayVariantB {...props} />;

  const {
    previewUrl,
    visible,
    ready,
    sceneFallback,
    canConfigure,
    loadFlags,
    error,
    canInstall,
    isIOS,
    onInstall,
    onAlign,
    onConfig,
    onRetryScene,
    onPlay,
    onWatchRoute,
  } = props;

  if (!visible) return null;
  const pending = [
    !loadFlags.manifest && "manifest",
    !loadFlags.trajectory && "route",
    !loadFlags.splat && !sceneFallback && "3D scene",
    !loadFlags.jet && "jet",
  ].filter(Boolean).join(", ");
  const status = sceneFallback
    ? "3D scene offline; fallback flight is ready."
    : error || (ready ? "Scene ready" : `Loading ${pending || "assets"}...`);

  return (
    <div
      className={`startup ${ready ? "is-ready" : ""} ${error ? "is-error" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="startup-title"
      aria-busy={!ready && !error}
    >
      <LoaderPreview url={previewUrl} />
      <div className="startup-card ui-panel">
        <strong id="startup-title">Experimental 3D reconstruction demo</strong>
        <p>Explore the Gaussian scene while the game keeps you inside the safe playable layer.</p>
        <div className="startup-card__status" role="status" aria-live="polite">
          {status}
        </div>
        <div className="startup-progress" aria-hidden="true"><i /></div>
        <div className="startup-card__actions">
          <button type="button" onClick={onWatchRoute} disabled={!ready}>Watch route</button>
          <button type="button" onClick={onAlign} disabled={!canConfigure}>Align</button>
          <button type="button" onClick={onConfig} disabled={!canConfigure}>Config</button>
          {error && <button type="button" onClick={onRetryScene}>Retry 3D</button>}
          <button type="button" onClick={onPlay} disabled={!ready}>{ready ? (sceneFallback ? "Play fallback" : "Play") : "Loading..."}</button>
        </div>
        {canInstall && <button className="install-button" type="button" onClick={onInstall}>Install app</button>}
        {isIOS && <span className="ios-note">Safari: Share, then Add to Home Screen.</span>}
      </div>
    </div>
  );
}
